import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, Column, Row, SortDescriptor, Table, TableBody, TableHeader, TableProps } from 'react-aria-components';
import {
  ColumnType,
  LeagueTableColumn,
  defaultLeagueTableColumns,
  mapLeagueTableColumnData,
  mapLeagueTableColumnsData,
} from './leagueTableColumns';
import { LeagueTableProps } from './contracts';
import { LinkSize, LinkVariant } from '../../../common/components/Link/contracts';
import Link from '../../../common/components/Link/Link';
import { Link as ReactLink } from 'react-router';
import { Image, ImageVariant } from '../../../common/components/Image';
import { ClubNameType } from '../../../fixtures/contracts';
import { RichTextPreview } from '../../../article/components/RichTextEditor/RichTextPreview';
import { LeagueTable } from '@repo/types';

// @ts-ignore fix vite import type
const isLeagueSite = import.meta.env.VITE_SITE_TYPE === 'league';

export type LeagueTableUIProps = Omit<LeagueTableProps, 'dataConfig'> & {
  leagueTable: LeagueTable;
  highlightedClubId?: string;
  className?: string;
  title?: string;
};

export default function LeagueTableUI({
  leagueTable,
  highlightedClubId,
  from = 1,
  to,
  limit = 5,
  nameType = ClubNameType.FullName,
  columns: externalColumns,
  linkLabel,
  linkUrl,
  linkTargetBlank,
  linkVariant = LinkVariant.Outlined,
  linkSize = LinkSize.m,
  tableGridLines,
  className,
  title,
}: LeagueTableUIProps) {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: LeagueTableColumn.Rank,
    direction: 'ascending',
  });

  const columns = externalColumns && externalColumns?.length > 0 ? externalColumns : defaultLeagueTableColumns;

  const initialDataToShow = useMemo(() => {
    // If on team detail page - show only teams around the selected team's position
    if (highlightedClubId && leagueTable) {
      const teamIndex = leagueTable.data.findIndex((tableRow: any) => {
        return tableRow.team?.id === highlightedClubId || tableRow.team?.club?.id === highlightedClubId;
      });

      // if the team does not exist in the table data
      if (teamIndex === -1) {
        return leagueTable.data;
      }

      const rowsOffset = limit % 2 === 0 ? limit / 2 : (limit - 1) / 2;

      // If the selected team is either first or second - show the top 5 teams
      if (teamIndex < rowsOffset) {
        return leagueTable?.data.slice(0, limit);
      }

      // If the selected team is either last or pre-last - show the bottom 5 teams
      if (leagueTable.data.length - teamIndex - 1 < rowsOffset) {
        return leagueTable.data.slice(leagueTable.data.length - limit, leagueTable.data.length);
      }

      // If the selected team is in the middle and there is enough room for offset
      return leagueTable.data.slice(teamIndex - rowsOffset, teamIndex + rowsOffset + 1);
    }

    if (!highlightedClubId && leagueTable) {
      const dataLimit = to ?? leagueTable.data.length;

      return leagueTable.data.slice(from - 1, dataLimit);
    }

    return leagueTable?.data ?? [];
  }, [from, highlightedClubId, limit, leagueTable, to]);

  const [dataToShow, setDataToShow] = useState<any[]>(initialDataToShow);

  const handleSortChange: TableProps['onSortChange'] = useCallback(
    (sortDescriptor: SortDescriptor) => {
      const column = sortDescriptor.column as LeagueTableColumn;

      if (dataToShow) {
        const sortedData = [...dataToShow].sort((a, b) => {
          const { data: aValue } = mapLeagueTableColumnData(column, a, nameType);
          const { data: bValue } = mapLeagueTableColumnData(column, b, nameType);

          if (aValue === bValue) {
            return 0;
          }

          if (sortDescriptor.direction === 'ascending') {
            return aValue > bValue ? 1 : -1;
          }

          return aValue < bValue ? 1 : -1;
        });

        setDataToShow(sortedData);
        setSortDescriptor({
          column,
          direction: sortDescriptor.direction,
        });
      }
    },
    [dataToShow, nameType]
  );

  useEffect(() => {
    setDataToShow(initialDataToShow);
  }, [initialDataToShow]);

  if (!leagueTable) {
    return null;
  }

  return (
    <div className={cn('league-division-table__wrapper', className)}>
      {title && <RichTextPreview content={title} />}
      <Table
        aria-label="League table"
        sortDescriptor={sortDescriptor}
        onSortChange={handleSortChange}
        className={cn('league-division-table', { 'league-division-table--grid-lines': tableGridLines })}
      >
        <TableHeader className="league-division-table__header">
          {columns.map((column, index) => (
            <Column
              key={column}
              id={column}
              className={cn('league-division-table__th', 'league-division-table__th--sortable', {
                'league-division-table--grid-lines__th': tableGridLines,
                'league-division-table__th--sorted': sortDescriptor.column === column,
                'league-division-table__th--sorted-ascending':
                  sortDescriptor.column === column && sortDescriptor.direction === 'ascending',
                'league-division-table__th--sorted-descending':
                  sortDescriptor.column === column && sortDescriptor.direction === 'descending',
              })}
              isRowHeader={index === 0}
              allowsSorting
            >
              {column}
            </Column>
          ))}
        </TableHeader>
        <TableBody className="league-division-table__body">
          {dataToShow &&
            dataToShow.map((row, index) => {
              const data = mapLeagueTableColumnsData(columns, row, nameType);

              return (
                <Row
                  key={row.team?.id ?? index}
                  className={cn('league-division-table__row', {
                    ['league-division-table__row--highlighted']:
                      row.team?.id === highlightedClubId || row.team?.club.id === highlightedClubId,
                  })}
                >
                  {data.map((item) => {
                    if (item.type === ColumnType.TeamName) {
                      return (
                        <Cell
                          key={item.label}
                          className={cn('league-division-table__cell', {
                            'league-division-table--grid-lines__cell': tableGridLines,
                          })}
                        >
                          {row.team?.slug ? (
                            <ReactLink
                              className="league-division-table__team-link"
                              to={isLeagueSite ? `/clubs/${row.team.club.slug}` : `/teams/${row.team.slug}`}
                            >
                              {row.team.logo && (
                                <Image
                                  variant={ImageVariant.SquareLogoSmall}
                                  src={row.team.logo}
                                  width={32}
                                  height={32}
                                  alt={row.team.fullName}
                                />
                              )}
                              {item.data}
                              {item.deductedPoints && <span className="league-division-table__deducted-points">*</span>}
                            </ReactLink>
                          ) : (
                            <span className="league-division-table__team-link">
                              {row.team?.logo && (
                                <Image
                                  variant={ImageVariant.SquareLogoSmall}
                                  src={row.team.logo}
                                  width={32}
                                  height={32}
                                  alt={row.team.fullName}
                                />
                              )}
                              {item.data}
                              {item.deductedPoints && <span className="league-division-table__deducted-points">*</span>}
                            </span>
                          )}
                        </Cell>
                      );
                    }

                    return (
                      <Cell
                        key={item.label}
                        className={cn('league-division-table__cell', {
                          'league-division-table--grid-lines__cell': tableGridLines,
                        })}
                      >
                        {item.data}
                      </Cell>
                    );
                  })}
                </Row>
              );
            })}
        </TableBody>
      </Table>
      {linkLabel && linkUrl && (
        <Link
          className="league-division-table__link"
          url={linkUrl}
          openInNewTab={!!linkTargetBlank}
          label={linkLabel}
          variant={linkVariant}
          size={linkSize}
        />
      )}
    </div>
  );
}
